<template>
  <div class="py-3 h-100 text-center">
    <div class="mb-3 px-3 py-2 bg-light text-dark rounded d-flex align-items-center justify-content-between">
      <div class="me-3">
        <span class="fs-3 fw-bold me-1">{{ dailyData.length }}</span>
        <span class="fs-6 fw-bold">
          SITE {{ dailyData.length > 1 ? "DIARIES" : "DIARY" }}
        </span>
      </div>

      <div>
        <!-- An extension extends an existing working period, so this applies
             from the second work order onwards. The first is created on the
             JKR Work Order step itself (INFS-1423). -->
        <button class="btn btn-outline-success mx-1" v-if="hasJkrWo"
          @click="$emit('extend-work-order')">
          Extension of Time
        </button>
        <button class="btn btn-success mx-1" @click="submitSiteDiary()"
          v-if="!nodStatus || nodStatus.site_diary !== true">
          Set as Complete
        </button>
      </div>
    </div>
    <v-calendar ref="siteDiaryCalendar" is-expanded is-dark mode="date" trim-weeks :attributes="calendarAttributes"
      :min-date="calendarBounds.min" :max-date="calendarBounds.max" :disabled-dates="gapDates"
      @dayclick="onDayClick" />

    <!-- Clickable legend: a chip filters the calendar to that work order's
         range; clicking it again (or All) restores every range. -->
    <div class="d-flex flex-wrap justify-content-center gap-2 mt-2 small" v-if="woRanges.length">
      <button class="badge border-0 text-white" :class="{ 'opacity-50': selectedWo != null }"
        style="cursor: pointer; background-color: #6c757d" v-if="woRanges.length > 1" @click="toggleWo(null)">
        All
      </button>
      <button class="badge border-0 text-white"
        :class="{ 'opacity-50': selectedWo != null && selectedWo != index }"
        :style="{ backgroundColor: rangeColorHex(index), cursor: 'pointer' }"
        v-for="(range, index) in woRanges" :key="`wo-legend-${index}`" @click="toggleWo(index)">
        WO{{ index + 1 }}: {{ Helper.formatDate(range.start) }} — {{ Helper.formatDate(range.end) }}
      </button>
    </div>

    <site-diary-form-modal :show="showSiteDiaryFormModal" :currentDate="currentDate" :nodData="nodData"
      :current-data="diaryToEdit" :getDistrictName="$parent.getDistrictName" :getRoadName="$parent.getRoadName"
      @close="closeSiteDiaryFormModal" />

    <pdf-viewer :url="currentPdf.url" :can-delete="currentPdf.canDelete" :can-edit="currentPdf.canEdit"
      v-if="currentPdf && !currentPdf?.isEditing" @close="currentPdf = null" @delete="deleteSiteDiary"
      @edit="editSiteDiary" />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import PdfViewer from "@/components/PdfViewer";
import SiteDiaryFormModal from "@/components/SiteDiaryFormModal";

export default {
  props: ["componentId", "nodData", "nodId", "nodStatus"],
  components: { SiteDiaryFormModal, PdfViewer },
  data() {
    return {
      showSiteDiaryFormModal: false,
      currentPdf: null,
      siteDiaryId: null,
      currentDate: null,
      diaryToEdit: null,
      dailyData: [],
      selectedWo: null,
    };
  },
  computed: {
    dates() {
      return this.dailyData.map((day) => day.date);
    },
    // Any work order at all, dated or not — unlike woRanges, which needs a
    // period. "Extension of Time" is about there being something to extend,
    // so a dateless legacy work order still counts.
    hasJkrWo() {
      return (
        Array.isArray(this.nodData && this.nodData.nod_jkr_ak) &&
        this.nodData.nod_jkr_ak.length > 0
      );
    },
    // Work-order working periods (INFS-1278). Dateless legacy work orders are
    // filtered out; when NO dated work order exists the calendar behaves as
    // before: unclamped, every date accepts entries.
    woRanges() {
      const wos = this.nodData && this.nodData.nod_jkr_ak;

      if (!Array.isArray(wos)) return [];

      return wos
        .filter((wo) => wo.start_date && wo.end_date)
        .map((wo) => ({ start: wo.start_date, end: wo.end_date }));
    },
    // Ranges the calendar currently works with — all of them, or just the
    // one picked in the legend.
    activeRanges() {
      if (this.selectedWo == null) {
        return this.woRanges.map((r, i) => ({ ...r, i }));
      }

      const r = this.woRanges[this.selectedWo];

      return r ? [{ ...r, i: this.selectedWo }] : [];
    },
    calendarBounds() {
      if (!this.activeRanges.length) return { min: null, max: null };

      const starts = this.activeRanges.map((r) => r.start).sort();
      const ends = this.activeRanges.map((r) => r.end).sort();

      return {
        min: new Date(starts[0]),
        max: new Date(ends[ends.length - 1]),
      };
    },
    // min/max-date grey out everything before the first range and after the
    // last, but days in a gap BETWEEN two ranges sit inside those bounds and
    // would render active. Disable the gaps so they grey out like the rest.
    // Overlapping ranges are merged first.
    gapDates() {
      if (this.activeRanges.length < 2) return null;

      const sorted = [...this.activeRanges].sort((a, b) =>
        String(a.start).localeCompare(String(b.start))
      );

      const merged = [];
      sorted.forEach((r) => {
        const last = merged[merged.length - 1];
        if (last && String(r.start).slice(0, 10) <= String(last.end).slice(0, 10)) {
          if (String(r.end).slice(0, 10) > String(last.end).slice(0, 10)) {
            last.end = r.end;
          }
        } else {
          merged.push({ start: r.start, end: r.end });
        }
      });

      const gaps = [];
      for (let i = 0; i < merged.length - 1; i++) {
        const gapStart = new Date(merged[i].end);
        gapStart.setDate(gapStart.getDate() + 1);
        const gapEnd = new Date(merged[i + 1].start);
        gapEnd.setDate(gapEnd.getDate() - 1);

        if (gapStart <= gapEnd) gaps.push({ start: gapStart, end: gapEnd });
      }

      return gaps.length ? gaps : null;
    },
    calendarAttributes() {
      // Range bands render under the diary-date highlights. Bands keep their
      // own work order's color even when the legend filters to one range.
      const rangeBands = this.activeRanges.map((range) => ({
        key: `wo-range-${range.i}`,
        highlight: { color: this.rangeColor(range.i), fillMode: "light" },
        dates: { start: new Date(range.start), end: new Date(range.end) },
        popover: {
          label: `Work Order ${range.i + 1}: ${this.Helper.formatDate(range.start)} — ${this.Helper.formatDate(range.end)}`,
        },
        order: -1,
      }));

      const diaryDates = this.dates.map((date) => ({
        highlight: true,
        dates: date,
      }));

      return [...rangeBands, ...diaryDates];
    },
  },
  methods: {
    rangeColor(index) {
      const colors = ["green", "purple", "orange", "teal"];
      return colors[index % colors.length];
    },
    // Legend chip colors matching v-calendar's named highlight palette.
    rangeColorHex(index) {
      const hexes = ["#10b981", "#8b5cf6", "#f97316", "#14b8a6"];
      return hexes[index % hexes.length];
    },
    // v-calendar's move() REJECTS when the target month falls outside
    // min/max-date — it does not resolve false. An unhandled rejection here
    // aborts the caller, so a diary dated outside every work-order period
    // used to abort getSiteDiaries() and leave the blocking spinner up
    // forever. Clamp into the allowed span, and still swallow a rejection.
    async moveCalendarTo(date) {
      const calendar = this.$refs.siteDiaryCalendar;

      if (!calendar || !date) return;

      const { min, max } = this.calendarBounds;
      let target = new Date(date);

      if (min && target < min) target = min;
      if (max && target > max) target = max;

      try {
        await calendar.move(target);
      } catch (e) {
        // Still unreachable (bounds moved under us). The calendar stays put;
        // never let this abort the caller.
      }
    },
    async toggleWo(index) {
      this.selectedWo = this.selectedWo === index ? null : index;

      const target =
        this.selectedWo != null ? this.woRanges[this.selectedWo] : null;

      if (target) {
        // Wait for the new min/max bounds to render first — move() cannot
        // reach a month outside the calendar's CURRENT bounds, which is
        // exactly the case when switching straight from one work order's
        // filter to another's.
        await this.$nextTick();

        await this.moveCalendarTo(target.start);
      }
    },
    dayInAnyRange(dayId) {
      if (!this.activeRanges.length) return true;

      // day.id and strapi date fields are both YYYY-MM-DD, so plain string
      // comparison is a correct day-level check.
      const day = String(dayId).slice(0, 10);

      return this.activeRanges.some(
        (range) =>
          day >= String(range.start).slice(0, 10) &&
          day <= String(range.end).slice(0, 10)
      );
    },
    onDayClick(day, type, data) {
      const hasDiary = this.dailyData
        .map((d) => this.Helper.formatDate(d.date))
        .includes(this.Helper.formatDate(day.id));

      // New entries only within a work-order period (INFS-1278). Existing
      // diaries stay viewable wherever they are, so legacy out-of-range
      // records are never hidden.
      if (type != 'edit' && !hasDiary && !this.dayInAnyRange(day.id)) return;

      if (type == 'edit' || (this.nodStatus.site_diary !== true &&
        !this.dailyData
          .map((d) => this.Helper.formatDate(d.date))
          .includes(this.Helper.formatDate(day.id)))) {
        this.currentDate = day.id;
        this.diaryToEdit = data;
        this.showSiteDiaryFormModal = true;
      } else {
        const diary = this.dailyData.find(
          (d) =>
            this.Helper.formatDate(d.date) == this.Helper.formatDate(day.id)
        );

        if (diary) {
          this.currentPdf = { id: diary.id, url: this.Helper.formatMediaUrl(diary.report) };

          if (this.nodStatus?.site_diary !== true) {
            this.currentPdf.canDelete = true;
            this.currentPdf.canEdit = true;
          }
        }
      }
    },
    async closeSiteDiaryFormModal(e) {
      if (e) {
        this.dailyData.push(e);

        this.Helper.toggleLoadingSpinner(true);

        const componentStatus = this.nodStatus;

        componentStatus.site_diary = "pending";

        await this.API.put(`nod-cases/${this.nodId}`, {
          componentStatus: componentStatus,
        });

        this.Helper.toggleLoadingSpinner(false);

        this.$emit("componentIsCompleted", this.siteDiaryId);
      }

      this.showSiteDiaryFormModal = false;

      this.currentDate = null;
    },
    triggerAlert(icon, text, callback, callbackData) {
      Swal.fire({
        icon: icon,
        title: `<h5>${text}</h5>`,
      }).then(() => {
        if (callback) {
          this.$emit(callback, callbackData);
        }
      });
    },
    async getSiteDiaries() {
      const [call, error] = await this.Helper.handlePromise(
        this.API.get(`nod-site-diaries?nod_case=${this.nodData.id}`)
      );

      if (error) {
        this.triggerAlert("error", "Error getting site diary data");
      } else {
        this.dailyData = call.data;

        if (this.dailyData.length) {
          await this.moveCalendarTo(this.dailyData[0].date);
        }
      }
    },
    async deleteSiteDiary() {
      Swal.fire({
        icon: "question",
        title: "<h5 class='fw-bold'>Delete this Site Diary?</h5>",
        html: "<p class='mb-0'>This action is not reversible.<br />Data cannot be retrieved after it is deleted.</p>",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.Helper.toggleLoadingSpinner(true);

          await this.API.del(`nod-site-visit/${this.currentPdf?.id}`);

          this.closeSiteDiaryFormModal();

          await this.getSiteDiaries();

          this.Helper.toggleLoadingSpinner(false);

          this.triggerAlert(
            "success",
            "Site diary deleted!"
          );
        }
      });
    },
    async editSiteDiary() {
      Swal.fire({
        icon: "question",
        title: "<h5 class='fw-bold'>Edit this Site Diary?</h5>",
        html: "<p class='mb-0'>This action is not reversible.<br />Data will be replaced after it is edited.</p>",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Edit",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const diary = this.dailyData.find(data => data.id == this.currentPdf.id);

          if (!diary) return;

          this.currentPdf.isEditing = true;
          this.currentPdf.data = diary;
          this.onDayClick({ id: diary.date }, 'edit', diary);
        }
      });
    },
    async submitSiteDiary() {
      // No rule existed for this before INFS-1278: completion was possible
      // with zero diaries. Require at least one.
      if (!this.dailyData.length) {
        return Swal.fire(
          "No site diary yet",
          "At least one site diary entry is required before setting as complete.",
          "warning"
        );
      }

      Swal.fire({
        icon: "question",
        title: "<h5 class='fw-bold'>Set Site Diary as Complete?</h5>",
        html: "<p class='mb-0'>This action is not reversible.<br />Data cannot be changed after it is submitted.</p>",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.Helper.toggleLoadingSpinner(true);

          const componentStatus = this.nodStatus;

          componentStatus.site_diary = true;

          await this.API.put(`nod-cases/${this.nodId}`, {
            componentStatus: componentStatus,
          });

          this.Helper.toggleLoadingSpinner(false);

          this.triggerAlert(
            "success",
            "Site diary updated!",
            "componentIsCompleted",
            this.siteDiaryId
          );
        }
      });
    },
  },
  async mounted() {
    this.Helper.toggleLoadingSpinner(true);

    this.siteDiaryId = this.componentId;

    try {
      await this.getSiteDiaries();
    } finally {
      // The spinner is a full-viewport overlay that also locks body scroll,
      // so it must come down on every path or the page is unusable.
      this.Helper.toggleLoadingSpinner(false);
    }
  },
};
</script>

<style lang="scss">
.vc-day {
  padding-block: 0.5rem;
}
</style>